import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Check, ChevronDown, ChevronRight, Copy } from 'lucide-react'
import { ActionPriorityPicker, PriorityTag } from '@/components/priority-picker'
import { PhoneNumber } from '@/components/phone-number'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { formatPhone } from '@/lib/phone'
import { cn, copyText, formatEventTime, formatMoney, formatStatus } from '@/lib/utils'
import {
  compactAge,
  isPastDue,
  shortDate,
  type ActionCard,
  type ActionFact,
  type ActionSourceEmail,
  type ActionInvoice,
  type ActionSourceShipment,
} from './action-card-model'
import { useActionItemSource } from './action-source'

export { PriorityTag }

/** Someone is working the item (USA-1443). */
export function InProgressTag() {
  return <StatusPill tone="progress" className="whitespace-nowrap">In progress</StatusPill>
}

function factText(kind: ActionFact['kind'], value: string) {
  if (kind === 'money') return formatMoney(value)
  if (kind === 'date') return shortDate(value)
  if (kind === 'percent') return `${value}%`
  return value
}

/** The number to call, tappable on a phone, with a button that copies it as shown (USA-1444). */
function PhoneFact({ phone }: { phone: string }) {
  const [copied, setCopied] = useState(false)
  const shown = formatPhone(phone)
  return (
    <span className="inline-flex items-center gap-1">
      <PhoneNumber phone={phone} />
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        className="-my-1 text-muted-foreground hover:text-foreground"
        aria-label={copied ? 'Copied' : `Copy ${shown}`}
        title={copied ? 'Copied' : 'Copy'}
        onClick={(event) => {
          void copyText(shown, event.currentTarget).then((done) => {
            if (!done) return
            setCopied(true)
            window.setTimeout(() => setCopied(false), 2000)
          })
        }}
      >
        {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      </Button>
    </span>
  )
}

/** The card's labelled values: Charge $245.00 est., Unit net $6.75 → $7.13, Ready date Not given. */
export function ActionFacts({ facts }: { facts: readonly ActionFact[] }) {
  if (facts.length === 0) return null
  return (
    <dl className="flex flex-wrap gap-x-7 gap-y-2">
      {facts.map((fact) => (
        <div key={fact.key} className="min-w-0">
          <dt className="text-[11px] font-medium text-muted-foreground">{fact.label}</dt>
          <dd
            className={cn(
              'truncate text-sm font-medium tabular-nums',
              fact.kind === 'code' && 'font-mono text-[13px]',
              fact.tone === 'warn' && 'text-amber-700 dark:text-amber-400',
              fact.tone === 'bad' && 'text-red-700 dark:text-red-400',
            )}
          >
            {fact.previous && (
              <>
                <s className="mr-1 font-normal text-muted-foreground">{factText(fact.kind, fact.previous)}</s>
                <span className="sr-only"> now </span>
              </>
            )}
            {fact.kind === 'phone' ? <PhoneFact phone={fact.value} /> : factText(fact.kind, fact.value)}
            {fact.qualifier && <span className="ml-1 text-xs font-normal text-muted-foreground">{fact.qualifier}</span>}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * The card's first line: priority (or how it was closed), its kind, then who owns it, when it is due and how
 * long ago it was raised.
 */
export function ActionCardTop({
  card,
  actionType,
  status,
  inProgress = false,
  owners,
  due,
  createdAt,
  priorityEdit,
}: {
  card?: ActionCard | null
  actionType: string
  status: string
  inProgress?: boolean
  owners: readonly string[]
  due?: string | null
  createdAt: string
  /** Where the item lives, so its priority can be picked from the card (USA-1451). */
  priorityEdit?: { source: string; uuid: string }
}) {
  const pastDue = due ? isPastDue(due) : false
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      {status === 'open' || status === 'in_progress' ? (
        <>
          {card &&
            (priorityEdit ? (
              <ActionPriorityPicker source={priorityEdit.source} actionUuid={priorityEdit.uuid} priority={card.priority} />
            ) : (
              <PriorityTag priority={card.priority} />
            ))}
          {inProgress && <InProgressTag />}
        </>
      ) : (
        <StatusPill tone={status === 'resolved' ? 'success' : 'neutral'}>{formatStatus(status)}</StatusPill>
      )}
      <span className="text-[13px] font-semibold text-foreground">
        {card?.kind_label ?? formatStatus(actionType)}
        {card?.sublabel && <span className="font-normal text-muted-foreground"> · {card.sublabel}</span>}
      </span>
      <span className="ml-auto flex items-center gap-1.5 text-muted-foreground">
        {owners.length > 0 && <span>{owners.join(', ')}</span>}
        {due && (
          <>
            <span aria-hidden className="text-border">·</span>
            <span className={cn('font-medium tabular-nums', pastDue ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400')}>
              {pastDue ? 'Overdue' : 'Due'} {shortDate(due)}
            </span>
          </>
        )}
        <span aria-hidden className="text-border">·</span>
        <time
          dateTime={createdAt}
          title={`Raised ${formatEventTime(createdAt)}`}
          className="font-medium tabular-nums text-foreground"
        >
          {compactAge(createdAt)}
        </time>
      </span>
    </div>
  )
}

/** A plain link to the conversation, opened at the email that raised the item: the PO's or the customer's. */
export function SourceEmailLink({ email, poId }: { email: ActionSourceEmail; poId: string }) {
  // A manufacturer data item's email is on the manufacturer's Emails tab (USA-1474).
  const source = useActionItemSource()
  const manufacturerUuid = source.kind === 'manufacturer_data' ? source.manufacturerUuid : null
  const sender = email.from_name?.trim()
  // A Quo call or text raises items too (USA-217): "Brittany Dray's call".
  const medium = email.medium ?? 'email'
  const label = (
    <>
      {/* Our own email (a chase item's ask) reads "our email", not the company's name (USA-1423). */}
      {sender && /^usa restaurant suppliers$/i.test(sender)
        ? `our ${medium}`
        : sender
          ? `${sender}'s ${medium}`
          : `the ${medium}`}
      {email.at && `, ${formatEventTime(email.at)}`} →
    </>
  )
  const className =
    'font-medium text-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground'
  if (manufacturerUuid) {
    return (
      <Link
        to="/manufacturers/$manuId"
        params={{ manuId: manufacturerUuid }}
        search={{ tab: 'emails', message: String(email.message_id) }}
        className={className}
      >
        {label}
      </Link>
    )
  }
  if (email.thread_kind === 'customer' && email.thread_id != null) {
    return (
      <Link
        to="/customer-comms/$threadId"
        params={{ threadId: String(email.thread_id) }}
        search={{ message: String(email.message_id) }}
        className={className}
      >
        {label}
      </Link>
    )
  }
  // A warranty item's email is on its claim's PO, with the warranty@ conversation (USA-915); an email no claim
  // holds yet has no page to open.
  const emailPoId = source.kind === 'warranty_claim' ? source.poUuid : poId
  if (emailPoId === null) return <span className="font-medium text-foreground">{label}</span>
  return (
    <Link
      to="/purchase-orders/$poId"
      params={{ poId: emailPoId }}
      search={{ message: String(email.message_id) }}
      className={className}
    >
      {label}
    </Link>
  )
}

/** A plain link to the shipment a system-raised item is about, e.g. "tracking 1Z6442360349104573 →" (USA-1464). */
export function SourceShipmentLink({ shipment }: { shipment: ActionSourceShipment }) {
  return (
    <Link
      to="/shipments/$shipmentId"
      params={{ shipmentId: shipment.uuid }}
      className="font-medium text-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
    >
      {shipment.label} →
    </Link>
  )
}

/** A plain link to the supplier invoice an accounting item is about, e.g. "Invoice 133473 →" (USA-103). */
export function ActionInvoiceLink({ invoice }: { invoice: ActionInvoice }) {
  return (
    <Link
      to="/invoices/$invoiceId"
      params={{ invoiceId: invoice.uuid }}
      className="font-medium text-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
    >
      {invoice.label}
      {invoice.paid && ', paid'} →
    </Link>
  )
}

/** The AI's full write-up, folded under the card until someone wants it; never repeats the title. */
export function ActionDetails({
  summary,
  details,
  headline,
}: {
  summary: string
  details?: string | null
  headline?: string | null
}) {
  const [open, setOpen] = useState(false)
  const showSummary = summary.trim() !== (headline ?? '').trim()
  if (!showSummary && !details) return null
  return (
    <div className="text-xs text-muted-foreground">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        {open ? <ChevronDown className="size-3.5" aria-hidden /> : <ChevronRight className="size-3.5" aria-hidden />}
        Details
      </button>
      {open && (
        <div className="mt-1.5 space-y-1.5 rounded-md bg-muted/50 px-3 py-2 text-sm text-foreground/80">
          {showSummary && <p className="break-words">{summary}</p>}
          {details && <p className="whitespace-pre-line break-words text-muted-foreground">{details}</p>}
        </div>
      )}
    </div>
  )
}
